'use client'
import * as React from 'react'
import {
  ArrowRight,
  Check,
  Loader2,
  Lock,
  Mail,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

// ─────────────────────────────────────────────────────────────────────────────
// UGC Revenue Calculator
//
// Two inputs only: industry + annual online sales ($0–$50M+, drag a slider).
// Output is a *band* (low / likely / high) of projected annual revenue uplift,
// broken into three UGC mechanisms (levers). States: empty → calculating →
// result → unlocked (an email gate reveals the full per-mechanism breakdown).
//
// ── THE MODEL (content-integrity critical) ──────────────────────────────────
//
// uplift_lever = annual_online_sales × pct(lever, industry)
//
// Each lever carries a per-industry [low, high] band, expressed as a percentage
// of annual online sales. The "likely" figure uses the band MIDPOINT; the
// headline low/likely/high band sums the per-lever low / midpoint / high.
//
// The three levers (mirroring the approved v2 proportions — conversion ≈ half,
// discovery ≈ a quarter, repeat ≈ a quarter):
//
//   1. Conversion lift        — shoppable customer content at the point of
//                               decision (the PDP). The largest lever.
//   2. Discovery lift         — fresh, crawlable UGC + reviews that classic
//                               SEO and AI answer engines (AEO) surface.
//   3. Repeat-purchase trust  — aggregated reviews lowering the perceived risk
//                               of the next order, lifting repeat rate / LTV.
//
// These are CONSERVATIVE, defensible ranges. Across industries the combined
// "likely" lands ≈ 3–6% of annual online sales, "low" ≈ 1.5–3%, "high"
// ≈ 7–12% — deliberately well below the optimistic single-figure claims that
// make a calculator untrustworthy. This is a REPRESENTATIVE ESTIMATE, not a
// guarantee, and the on-page methodology says so plainly.
//
// Mirrors ugcRevenueEstimate()/REVENUE_INDUSTRIES in
// nextapp/lib/publicToolsMath.ts (nextapp's white-label copy for Feefo, see
// this repo's CLAUDE.md "Public calculator tools") — the INDUSTRIES bands
// below are content-integrity-sensitive, keep both copies byte-identical.
//
// Named public sources behind the ranges (consolidated, not invented):
//   • Spiegel Research Center (Northwestern Kellogg) — reviews lift conversion,
//     with the effect largest on higher-consideration and higher-priced items.
//   • PowerReviews / Northwestern benchmark reports — conversion uplift when
//     shoppers interact with ratings, reviews and customer photos/video on PDPs.
//   • Baymard Institute — PDP UX + social-proof research on why shoppers stall
//     and what recovers the add-to-cart.
//   • Consumer review-trust surveys (BrightLocal, Bazaarvoice) — how reviews
//     and customer content shape purchase and repeat-purchase decisions.
// The per-industry spread reflects category sensitivity (beauty / fashion read
// social proof harder than commodity electronics), kept inside the same band.
// ─────────────────────────────────────────────────────────────────────────────

type Lever = 'conversion' | 'discovery' | 'repeat'

type IndustryDef = {
  id: string
  name: string
  // Per-lever [low%, high%] of annual online sales.
  bands: Record<Lever, [number, number]>
}

// Bands are percentages of annual online sales. Conservative by design.
const INDUSTRIES: IndustryDef[] = [
  { id: 'fashion',     name: 'Fashion & apparel',     bands: { conversion: [2.0, 4.0], discovery: [0.9, 1.8], repeat: [1.0, 2.2] } },
  { id: 'beauty',      name: 'Beauty & cosmetics',    bands: { conversion: [2.2, 4.4], discovery: [1.0, 2.0], repeat: [1.2, 2.4] } },
  { id: 'jewelry',     name: 'Accessories & jewelry', bands: { conversion: [1.8, 3.6], discovery: [0.8, 1.6], repeat: [0.9, 1.8] } },
  { id: 'furniture',   name: 'Furniture & home',      bands: { conversion: [1.8, 3.4], discovery: [0.8, 1.6], repeat: [0.7, 1.4] } },
  { id: 'hospitality', name: 'Hospitality & travel',  bands: { conversion: [1.6, 3.2], discovery: [1.0, 2.0], repeat: [0.9, 1.8] } },
  { id: 'electronics', name: 'Consumer electronics',  bands: { conversion: [1.4, 2.8], discovery: [0.8, 1.5], repeat: [0.6, 1.2] } },
  { id: 'sporting',    name: 'Sporting goods',        bands: { conversion: [1.7, 3.4], discovery: [0.8, 1.6], repeat: [0.8, 1.6] } },
  { id: 'food',        name: 'Food & beverage',       bands: { conversion: [1.5, 3.0], discovery: [0.9, 1.7], repeat: [1.0, 2.0] } },
]

const LEVER_META: Record<Lever, { label: string; sub: string }> = {
  conversion: { label: 'Conversion lift',       sub: 'shoppable UGC at the PDP' },
  discovery:  { label: 'Discovery lift',        sub: 'fresh UGC indexed for SEO + AEO' },
  repeat:     { label: 'Repeat-purchase trust', sub: 'reviews that de-risk the next buy' },
}

const LEVER_ORDER: Lever[] = ['conversion', 'discovery', 'repeat']

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Slider is in $M, 0–50, half-million steps. 50 reads as "$50M+".
const MAX_SALES_M = 50

function moneyCompact(n: number): string {
  const v = Math.max(0, Math.round(n))
  if (v >= 1e6) {
    const m = v / 1e6
    return '$' + (m >= 10 ? m.toFixed(1) : m.toFixed(2)) + 'M'
  }
  if (v >= 1e3) return '$' + Math.round(v / 1e3) + 'K'
  return '$' + v.toLocaleString('en-US')
}

function salesLabel(m: number): string {
  if (m >= MAX_SALES_M) return '$50M+'
  if (m >= 1) return '$' + (Number.isInteger(m) ? m : m.toFixed(1)) + 'M'
  if (m <= 0) return '$0'
  return '$' + Math.round(m * 1000) + 'K'
}

function rangeChip(band: [number, number]): string {
  return `+${band[0]}–${band[1]}%`
}

type LeverResult = { lever: Lever; band: [number, number]; low: number; mid: number; high: number }
type Estimate = {
  low: number
  likely: number
  high: number
  levers: LeverResult[]
}

function computeEstimate(industry: IndustryDef, salesM: number): Estimate {
  const sales = Math.min(salesM, MAX_SALES_M) * 1e6
  const levers: LeverResult[] = LEVER_ORDER.map((lever) => {
    const band = industry.bands[lever]
    const low = sales * (band[0] / 100)
    const high = sales * (band[1] / 100)
    return { lever, band, low, mid: (low + high) / 2, high }
  })
  return {
    low: levers.reduce((s, l) => s + l.low, 0),
    likely: levers.reduce((s, l) => s + l.mid, 0),
    high: levers.reduce((s, l) => s + l.high, 0),
    levers,
  }
}

type Phase = 'empty' | 'calculating' | 'result'
type Gate = 'locked' | 'sending' | 'unlocked'

export function UgcRevenueCalculator() {
  const [industryId, setIndustryId] = React.useState(INDUSTRIES[0].id)
  const [salesM, setSalesM] = React.useState(5)
  const [phase, setPhase] = React.useState<Phase>('empty')
  const [gate, setGate] = React.useState<Gate>('locked')
  const [display, setDisplay] = React.useState(0)
  const [email, setEmail] = React.useState('')
  const [gateError, setGateError] = React.useState<string | null>(null)

  const rafRef = React.useRef<number | null>(null)
  const calcTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const liveRef = React.useRef<HTMLSpanElement | null>(null)

  const industry = React.useMemo(
    () => INDUSTRIES.find((i) => i.id === industryId) ?? INDUSTRIES[0],
    [industryId],
  )

  // The result is frozen at "Calculate" time so dragging the slider afterwards
  // does not silently re-shift the revealed numbers; changing an input resets
  // to the empty state instead.
  const [frozen, setFrozen] = React.useState<Estimate | null>(null)
  const estimate = frozen ?? computeEstimate(industry, salesM)

  const prefersReduced = React.useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  React.useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (calcTimer.current) clearTimeout(calcTimer.current)
    }
  }, [])

  function countUp(target: number) {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    const dur = 720
    const t0 = performance.now()
    const ease = (x: number) => 1 - Math.pow(1 - x, 3)
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / dur)
      setDisplay(target * ease(p))
      if (p < 1) rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
  }

  function resetToEmpty() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (calcTimer.current) clearTimeout(calcTimer.current)
    setFrozen(null)
    setPhase('empty')
    setGate('locked')
    setDisplay(0)
    setGateError(null)
  }

  function onIndustry(e: React.ChangeEvent<HTMLSelectElement>) {
    setIndustryId(e.target.value)
    if (phase === 'result' || phase === 'calculating') resetToEmpty()
  }

  function onSlide(v: number) {
    setSalesM(v)
    if (phase === 'result' || phase === 'calculating') resetToEmpty()
  }

  function onCalculate() {
    if (phase === 'calculating') return
    setPhase('calculating')
    setGate('locked')
    if (calcTimer.current) clearTimeout(calcTimer.current)
    const reduced = prefersReduced()
    calcTimer.current = setTimeout(
      () => {
        const next = computeEstimate(industry, salesM)
        setFrozen(next)
        setPhase('result')
        if (reduced) {
          setDisplay(next.likely)
        } else {
          setDisplay(0)
          countUp(next.likely)
        }
        // Move focus context for screen readers without stealing the page.
        requestAnimationFrame(() => liveRef.current?.focus?.())
      },
      reduced ? 200 : 900,
    )
  }

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault()
    if (gate === 'sending') return
    const clean = email.trim()
    if (!EMAIL_RE.test(clean)) {
      setGateError('Enter a valid work email so we can send the breakdown.')
      return
    }
    setGate('sending')
    setGateError(null)
    // Reuse the existing lead path (the same /api/leads endpoint
    // LeadCaptureForm posts to), passing the calculator context so the
    // follow-up email + CRM record carry the industry, sales band and the
    // likely number the visitor saw. The reveal is the value exchange — never
    // punish a real email for a backend hiccup, so we unlock regardless.
    try {
      await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: clean,
          intent: 'try-tool',
          source: '/tools/ugc-revenue-calculator',
          toolSlug: 'ugc-revenue-calculator',
          message: `UGC Revenue Calculator — industry: ${industry.name}; annual online sales: ${salesLabel(salesM)}; likely uplift: ${moneyCompact(estimate.likely)} (low ${moneyCompact(estimate.low)} / high ${moneyCompact(estimate.high)}).`,
        }),
        signal: AbortSignal.timeout(15_000),
      }).catch(() => {})
    } catch {
      /* swallow — unlock below regardless */
    }
    setGate('unlocked')
  }

  const showResult = phase === 'result'
  const sliderPct = Math.min(100, (salesM / MAX_SALES_M) * 100)
  // The likely marker is placed proportionally inside the low→high band.
  const bandLowPct = estimate.high > 0 ? Math.round((estimate.low / estimate.high) * 100) : 35
  const bandLikelyPct = estimate.high > 0 ? Math.round((estimate.likely / estimate.high) * 100) : 64

  return (
    <div className="w-full">
      {/* ── INPUT CARD (light) — empty + calculating live here ───────────── */}
      {!showResult && (
        <div className="rounded-3xl border border-border bg-card p-6 shadow-xl shadow-primary/5 sm:p-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            Estimate your uplift
          </p>

          <div className="mt-6">
            <label htmlFor="ugc-industry" className="block text-sm font-semibold text-foreground">
              Your industry
            </label>
            <div className="relative mt-2.5">
              <select
                id="ugc-industry"
                value={industryId}
                onChange={onIndustry}
                className="h-12 w-full appearance-none rounded-xl border border-input bg-secondary/40 px-4 pr-11 text-base font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {INDUSTRIES.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                  </option>
                ))}
              </select>
              <svg
                aria-hidden
                viewBox="0 0 20 20"
                className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 10 13 14 9" />
              </svg>
            </div>
          </div>

          <div className="mt-7">
            <div className="flex items-end justify-between gap-3">
              <label htmlFor="ugc-sales" className="text-sm font-semibold text-foreground">
                Annual online sales
              </label>
              <span className="font-display text-3xl font-medium leading-none tabular-nums text-foreground">
                {salesLabel(salesM)}
              </span>
            </div>
            <input
              id="ugc-sales"
              type="range"
              min={0}
              max={MAX_SALES_M}
              step={0.5}
              value={salesM}
              onChange={(e) => onSlide(Number(e.target.value))}
              aria-label="Annual online sales in US dollars"
              aria-valuetext={salesLabel(salesM)}
              className="ugc-rev-slider mt-3.5"
              style={{
                background: `linear-gradient(90deg, hsl(var(--primary)) ${sliderPct}%, hsl(var(--secondary)) ${sliderPct}%)`,
              }}
            />
            <div className="mt-2.5 flex justify-between text-xs text-muted-foreground">
              <span>$0</span>
              <span>$50M+</span>
            </div>
          </div>

          <Button
            type="button"
            size="lg"
            onClick={onCalculate}
            disabled={phase === 'calculating'}
            className="mt-7 w-full rounded-full text-base font-semibold"
          >
            {phase === 'calculating' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Calculating
              </>
            ) : (
              'Calculate my uplift'
            )}
          </Button>
          <p className="mt-3.5 text-center text-xs text-muted-foreground">
            Free instant estimate. Email unlocks the full breakdown.
          </p>
        </div>
      )}

      {/* ── RESULT CARD (navy/lime punctuation, homepage-style) ──────────── */}
      {showResult && (
        <div className="ugc-rev-rise relative overflow-hidden rounded-3xl border border-brand-lime/20 bg-brand-navy p-6 text-white shadow-2xl sm:p-8">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full"
            style={{ background: 'radial-gradient(circle, hsl(var(--brand-lime) / 0.14), transparent 70%)' }}
          />
          <div className="relative">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-lime">
              Your estimate · {industry.name}
            </p>
            <p className="mt-3.5 text-sm text-white/60">You could be leaving up to</p>
            <div className="flex flex-wrap items-baseline gap-x-2.5">
              <span
                ref={liveRef}
                tabIndex={-1}
                aria-live="polite"
                className="font-display text-[clamp(3.25rem,11vw,5.25rem)] font-medium leading-[0.95] tracking-tight text-brand-lime outline-none"
              >
                {moneyCompact(display)}
              </span>
              <span className="text-lg font-medium text-white/55 sm:text-xl">/ year</span>
            </div>
            <p className="mt-1.5 text-sm text-white/60">on the table in unrealised revenue.</p>

            {/* low / likely / high band */}
            <div className="mb-1.5 mt-6">
              <div className="relative h-2.5 rounded-full bg-white/10">
                <div
                  className="absolute inset-y-0 rounded-full bg-brand-lime/30"
                  style={{ left: `${bandLowPct}%`, right: '6px' }}
                />
                <div
                  className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-brand-navy bg-brand-lime shadow"
                  style={{ left: `${bandLikelyPct}%` }}
                />
              </div>
              <div className="mt-2.5 flex justify-between text-xs">
                <span className="text-white/50">
                  low <span className="font-semibold text-white">{moneyCompact(estimate.low)}</span>
                </span>
                <span className="text-white/50">
                  likely <span className="font-semibold text-brand-lime">{moneyCompact(estimate.likely)}</span>
                </span>
                <span className="text-white/50">
                  high <span className="font-semibold text-white">{moneyCompact(estimate.high)}</span>
                </span>
              </div>
            </div>

            {/* per-lever rows */}
            <div className="mt-5 border-t border-white/10 pt-1">
              {estimate.levers.map((l) => (
                <div
                  key={l.lever}
                  className="flex items-center justify-between gap-3.5 border-b border-white/[0.07] py-3.5"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white">{LEVER_META[l.lever].label}</div>
                    <div className="mt-0.5 text-xs text-white/50">{LEVER_META[l.lever].sub}</div>
                  </div>
                  <div className="flex flex-none items-center gap-3">
                    <span className="whitespace-nowrap rounded-full border border-dashed border-brand-lime/40 bg-brand-lime/10 px-2.5 py-1 text-[11.5px] font-semibold text-brand-lime">
                      {rangeChip(l.band)}
                    </span>
                    <span className="min-w-16 text-right text-[15px] font-semibold text-white">
                      {moneyCompact(l.mid)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3.5">
              <p className="m-0 max-w-[64%] text-[11.5px] leading-relaxed text-white/40">
                Representative estimate, based on named industry benchmark ranges. Not a guarantee.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={resetToEmpty}
                className="flex-none rounded-full border-white/20 bg-transparent text-white hover:border-brand-lime hover:bg-white/5 hover:text-brand-lime"
              >
                <RotateCcw className="mr-1.5 h-4 w-4" /> Recalculate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── EMAIL GATE + UNLOCKED (light) ────────────────────────────────── */}
      {showResult && (
        <div className="ugc-rev-rise mx-auto mt-7 w-full max-w-[760px]">
          {gate !== 'unlocked' ? (
            <div className="grid items-center gap-6 rounded-3xl border border-border bg-card p-6 shadow-xl shadow-primary/5 sm:p-8 md:grid-cols-2">
              <div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  <Lock className="h-3.5 w-3.5" /> Unlock the detail
                </span>
                <h3 className="mt-2.5 font-display text-2xl font-medium leading-snug tracking-tight text-foreground">
                  Get your personalised breakdown plus 90-day plan
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground text-pretty">
                  Per-mechanism low, likely and high, the assumptions behind each, and a sequenced
                  rollout for your store.
                </p>
              </div>
              <form onSubmit={onSubmitEmail}>
                <label htmlFor="ugc-email" className="block text-sm font-semibold text-foreground">
                  Work email
                </label>
                <Input
                  id="ugc-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  aria-label="Work email for the full breakdown"
                  className="mt-2.5 h-12 rounded-xl border-input bg-secondary/40 text-base"
                />
                <Button
                  type="submit"
                  size="lg"
                  disabled={gate === 'sending'}
                  className="mt-3 w-full rounded-full text-[15px] font-semibold"
                >
                  {gate === 'sending' ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      <Mail className="mr-1.5 h-4 w-4" /> Email me the breakdown
                    </>
                  )}
                </Button>
                {gateError && (
                  <p role="alert" className="mt-2 text-xs text-destructive">
                    {gateError}
                  </p>
                )}
                <p className="mt-2.5 text-center text-[11.5px] text-muted-foreground">
                  No spam. One email with your breakdown and plan.
                </p>
              </form>
            </div>
          ) : (
            <div className="ugc-rev-rise rounded-3xl border border-border bg-card p-6 shadow-xl shadow-primary/5 sm:p-8">
              <div className="mb-1.5 flex items-center gap-2.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--accent-ink))]">
                  Unlocked
                </span>
              </div>
              <h3 className="font-display text-2xl font-medium leading-snug tracking-tight text-foreground">
                Your full breakdown is on its way
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Sent to <span className="font-semibold text-foreground">{email}</span>. Here is the detail
                behind your {moneyCompact(estimate.likely)} estimate.
              </p>

              {/* detailed low / likely / high table */}
              <div className="mt-5 overflow-hidden rounded-2xl border border-border">
                <div className="grid grid-cols-[1.7fr_1fr_1fr_1fr] bg-secondary/50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  <span>Mechanism</span>
                  <span className="text-right">Low</span>
                  <span className="text-right">Likely</span>
                  <span className="text-right">High</span>
                </div>
                {estimate.levers.map((l) => (
                  <div
                    key={l.lever}
                    className="grid grid-cols-[1.7fr_1fr_1fr_1fr] items-center border-t border-border px-4 py-3.5"
                  >
                    <span className="text-sm font-semibold text-foreground">{LEVER_META[l.lever].label}</span>
                    <span className="text-right text-sm text-muted-foreground">{moneyCompact(l.low)}</span>
                    <span className="text-right text-sm font-semibold text-foreground">{moneyCompact(l.mid)}</span>
                    <span className="text-right text-sm text-muted-foreground">{moneyCompact(l.high)}</span>
                  </div>
                ))}
                <div className="grid grid-cols-[1.7fr_1fr_1fr_1fr] items-center border-t border-border bg-brand-navy px-4 py-3.5">
                  <span className="text-sm font-semibold text-white">Total uplift</span>
                  <span className="text-right text-sm text-white/60">{moneyCompact(estimate.low)}</span>
                  <span className="text-right text-[15px] font-bold text-brand-lime">
                    {moneyCompact(estimate.likely)}
                  </span>
                  <span className="text-right text-sm text-white/60">{moneyCompact(estimate.high)}</span>
                </div>
              </div>

              {/* 90-day plan */}
              <div className="mt-6">
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  Your 90-day plan
                </span>
                <div className="mt-3.5 grid gap-3 sm:grid-cols-3">
                  {[
                    { n: '01', h: 'Days 0–30: collect', b: 'Aggregate and rights-clear UGC from social and reviews.' },
                    { n: '02', h: 'Days 30–60: embed', b: 'Launch shoppable galleries on PDPs and the homepage.' },
                    { n: '03', h: 'Days 60–90: measure', b: 'Track lift against the band and tune placement.' },
                  ].map((s) => (
                    <div key={s.n} className="rounded-2xl bg-secondary/40 p-4">
                      <div className="font-display text-2xl font-semibold text-[hsl(var(--accent-ink))]">{s.n}</div>
                      <div className="mt-1.5 text-sm font-semibold text-foreground">{s.h}</div>
                      <div className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground text-pretty">{s.b}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button asChild size="lg" className="rounded-full font-semibold">
                  <a href="https://app.idukki.io/register?source=ugc-revenue-calculator">
                    Get started <ArrowRight className="ml-1.5 h-4 w-4" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full">
                  <a href="https://app.apollo.io/#/meet/idukki/Rohin" target="_blank" rel="noopener noreferrer">
                    Book a strategy call
                  </a>
                </Button>
              </div>
              <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-[hsl(var(--accent-ink))]" /> No-code. Live on Shopify,
                WooCommerce, BigCommerce and more.
              </p>
            </div>
          )}
        </div>
      )}

      <style>{`
        .ugc-rev-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 10px;
          border-radius: 999px;
          outline: none;
          cursor: pointer;
        }
        .ugc-rev-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: hsl(var(--card));
          border: 4px solid hsl(var(--primary));
          box-shadow: 0 6px 16px hsl(var(--primary) / 0.28);
          cursor: grab;
          transition: transform 160ms cubic-bezier(.2,1.4,.4,1);
        }
        .ugc-rev-slider::-webkit-slider-thumb:active { transform: scale(1.12); cursor: grabbing; }
        .ugc-rev-slider::-moz-range-thumb {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: hsl(var(--card));
          border: 4px solid hsl(var(--primary));
          box-shadow: 0 6px 16px hsl(var(--primary) / 0.28);
          cursor: grab;
        }
        .ugc-rev-slider:focus-visible {
          box-shadow: 0 0 0 2px hsl(var(--background)), 0 0 0 4px hsl(var(--ring));
        }
        .ugc-rev-rise { animation: ugcRevRise 280ms cubic-bezier(.2,.9,.25,1) both; }
        @keyframes ugcRevRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .ugc-rev-rise { animation: none; }
          .ugc-rev-slider::-webkit-slider-thumb { transition: none; }
        }
      `}</style>
    </div>
  )
}
